import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactInputMask from "react-input-mask";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { useForm } from "react-hook-form";
import {
  additionalServices,
  initialState,
  servicePlan,
  servicesPosition,
  tashkentDistricts,
} from "./data";
import { schema } from "./schema";

export default function Form() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialState,
  });

  useEffect(() => {
    setValue("servicePlan", "");
    setValue("servicesPosition", "");
    setValue("carpet", "");
    setValue("dishwashing", "");
    setValue("furniture", "");
    setValue("insideFridge", "");
    setValue("insideOven", "");
    setValue("window", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("servicesType")]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await fetch("https://informer.ez-app.online/v1/send-manino-quote", {
        method: "POST",
        headers: {
          "Content-Type": "aplication/json",
        },
        body: JSON.stringify({
          message: `
<b>🧹 Service Details</b>
<b>• Type:</b> <i>${data.servicesType}</i>
<b>• Position:</b> <i>${data.servicesPosition || "—"}</i>
${data.servicePlan ? `<b>• Plan:</b> <i>${data.servicePlan}</i>` : ""}

${
  data.insideFridge ||
  data.insideOven ||
  data.window ||
  data.dishwashing ||
  data.furniture ||
  data.carpet
    ? `<b>• Additional Services:</b>
${data.insideFridge ? `<i>   - ${data.insideFridge}</i>\n` : ""}${
        data.insideOven ? `<i>   - ${data.insideOven}</i>\n` : ""
      }${data.window ? `<i>   - ${data.window}</i>\n` : ""}${
        data.dishwashing ? `<i>   - ${data.dishwashing}</i>\n` : ""
      }${data.furniture ? `<i>   - ${data.furniture}</i>\n` : ""}${
        data.carpet ? `<i>   - ${data.carpet}</i>\n` : ""
      }`
    : ""
}
<b>👤 Client Info</b>
<b>• Name:</b> <i>${data.firstName}</i>
<b>• Phone:</b> <i>${data.tel}</i>

<b>📍 Location</b>
<b>• Address:</b> <i>${data.address}</i>
${
  data.tashkentDistricts
    ? `<b>• District:</b> <i>${data.tashkentDistricts}</i>`
    : ""
}
${data.state ? `<b>• State:</b> <i>${data.state}</i>` : ""}
${data.city ? `<b>• City:</b> <i>${data.city}</i>` : ""}
${data.suit ? `<b>• Apt/Suit:</b> <i>${data.suit}</i>` : ""}
  `.trim(),
        }),
      });
      reset();
      navigate("success");
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        id="clean-form"
      >
        <div className="row mb-2 g-3">
          {/* servicesType */}
          <div
            className={
              watch("servicesType") === "Moving"
                ? "col-md-8"
                : !watch("servicesType")
                ? "col-md-12"
                : "col-md-4"
            }
          >
            {/* <label htmlFor="servicesType" className="form-label">
              Services Type
            </label> */}
            <select
              id="servicesType"
              className={`form-select ${
                errors?.servicesType?.message ? "is-invalid" : "is-valid"
              }`}
              {...register("servicesType")}
            >
              <option value="" disabled>
                {t("Quote.select")}
              </option>
              <option value="Residential Cleaning">
                {t("Quote.ServicesType.residential")}
              </option>
              <option value="Commercial Cleaning">
                {t("Quote.ServicesType.commercial")}
              </option>
              <option value="Moving"> {t("Quote.ServicesType.moving")}</option>
            </select>
            {errors?.servicesType && (
              <span className="text-danger small">
                {errors?.servicesType?.message}
              </span>
            )}
          </div>

          {/* servicesPosition */}
          {watch("servicesType") && (
            <div className="col-md-4">
              {/* <label htmlFor="servicesPosition" className="form-label">
              Services Position
            </label> */}
              <select
                id="servicesPosition"
                className={`form-select ${
                  errors?.servicesPosition?.message ? "is-invalid" : "is-valid"
                }`}
                {...register("servicesPosition")}
              >
                {(watch("servicesType") === "Moving"
                  ? servicesPosition.moving
                  : servicesPosition.cleaning
                ).map((item) => (
                  <option
                    key={item.id}
                    value={item.value}
                    disabled={item?.disabled}
                    selected={item?.selected}
                  >
                    {t(item.option)}
                  </option>
                ))}
              </select>
              {errors?.servicesPosition && (
                <span className="text-danger small">
                  {errors?.servicesPosition?.message}
                </span>
              )}
            </div>
          )}

          {/* servicePlan */}
          {watch("servicesType") !== "Moving" && watch("servicesType") && (
            <div className="col-md-4">
              {/* <label htmlFor="servicePlan" className="form-label">
              Service Plan
            </label> */}
              <select
                id="servicePlan"
                className={`form-select ${
                  errors?.servicePlan?.message ? "is-invalid" : "is-valid"
                }`}
                {...register("servicePlan")}
              >
                {servicePlan.map((item) => (
                  <option
                    key={item.id}
                    value={item.value}
                    disabled={item?.disabled ? item?.disabled : false}
                    selected={item?.selected ? item?.selected : false}
                  >
                    {t(item.option)}
                  </option>
                ))}
              </select>
              {errors?.servicePlan && (
                <span className="text-danger small">
                  {errors?.servicePlan?.message}
                </span>
              )}
            </div>
          )}
        </div>

        {/* additional services */}
        {watch("servicesType") !== "Moving" && watch("servicesType") && (
          <div className="border border-success rounded pt-2 pb-2 mt-3 mb-3">
            <div className="d-flex flex-wrap gap-3">
              {additionalServices.map(({ id, key, value, forLanguage }) => (
                <div
                  key={id}
                  className="form-check d-flex align-items-center gap-2 rounded"
                >
                  <input
                    className="form-check-input is-valid m-0"
                    type="checkbox"
                    id={key}
                    value={value}
                    {...register(key)}
                  />
                  <label className="form-check-label fs-6" htmlFor={key}>
                    {t(forLanguage)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* numberOfRooms */}
        <div className="mb-2">
          <label htmlFor="numberOfRooms" className="form-label">
            {t("Quote.numberOfRooms")}
          </label>
          <input
            id="numberOfRooms"
            type="number"
            min={0}
            placeholder={t("Quote.numberOfRoomsPh")}
            className={`form-control ${
              errors?.numberOfRooms?.message ? "is-invalid" : "is-valid"
            }`}
            {...register("numberOfRooms")}
          />
          {errors?.numberOfRooms && (
            <span className="text-danger small">
              {errors?.numberOfRooms?.message}
            </span>
          )}
        </div>

        <div className="row mb-2 g-3">
          {/* firstName */}
          <div className="col-md-6">
            <label htmlFor="firstName" className="form-label">
              {t("Quote.fullName")}
            </label>
            <input
              className={`form-control ${
                errors?.firstName?.message ? "is-invalid" : "is-valid"
              }`}
              id="firstName"
              type="text"
              placeholder={t("Quote.fullNamePh")}
              {...register("firstName")}
            />
            {errors?.firstName && (
              <span className="text-danger small">
                {errors?.firstName?.message}
              </span>
            )}
          </div>

          {/* tashkentDistricts */}
          <div className="col-md-6">
            <label htmlFor="tashkentDistricts" className="form-label">
              {t("Quote.district.label")}
            </label>
            <select
              id="tashkentDistricts"
              className={`form-select ${
                errors?.tashkentDistricts ? "is-invalid" : "is-valid"
              }`}
              {...register("tashkentDistricts")}
            >
              {tashkentDistricts.map(
                ({ id, value, selected = false, disabled = false, option }) => {
                  return (
                    <option
                      key={id}
                      value={value}
                      selected={selected}
                      disabled={disabled}
                    >
                      {t(option)}
                    </option>
                  );
                }
              )}
            </select>
            {errors?.tashkentDistricts && (
              <span className="text-danger small">
                {errors?.tashkentDistricts?.message}
              </span>
            )}
          </div>
        </div>

        <div className="mb-2">
          <label htmlFor="tel" className="form-label">
            {t("Quote.phone")}
          </label>

          <ReactInputMask
            mask="+\9\9\8 99 999 99 99"
            autoComplete="off"
            maskChar="_"
            {...register("tel")}
          >
            {(inputProps) => (
              <input
                {...inputProps}
                type="tel"
                className={`form-control ${
                  errors?.tel?.message ? "is-invalid" : "is-valid"
                }`}
                placeholder="+998 12 345 67 89"
              />
            )}
          </ReactInputMask>

          {errors?.tel && (
            <span className="text-danger small">{errors?.tel?.message}</span>
          )}
        </div>

        {/* address */}
        <div className="mb-2">
          <label htmlFor="message" className="form-label">
            {t("Quote.address")}
          </label>
          <textarea
            id="message"
            className={`form-control ${
              errors?.address?.message ? "is-invalid" : "is-valid"
            }`}
            rows="3"
            placeholder={t("Quote.addressPh")}
            {...register("address")}
          />
          {errors?.address && (
            <span className="text-danger small">
              {errors?.address?.message}
            </span>
          )}
        </div>
      </form>

      <button
        type="submit"
        form="clean-form"
        disabled={isLoading}
        className="btn btn-success"
      >
        {t("Quote.submit")}
      </button>
    </div>
  );
}
